import { normalizeMarkdownCodeBlockAttrsInSlice } from '@atlaskit/editor-common/code-block';
import { logException } from '@atlaskit/editor-common/monitoring/error';
import { md } from '@atlaskit/editor-common/paste/md';
import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { Fragment, Slice } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { Selection } from '@atlaskit/editor-prosemirror/state';
import { ReplaceStep } from '@atlaskit/editor-prosemirror/transform';

import type { MarkdownToPmConverter } from '../../pasteOptionsToolbarPluginType';
import type { PasteOptionsPluginState } from '../../types/types';
import { escapeLinks } from './index';

// Ignored via go/ees005
// eslint-disable-next-line require-unicode-regexp
const CODE_BLOCK_FENCE_REGEX = /```/;

export const formatMarkdown = (
	tr: Transaction,
	pluginState: PasteOptionsPluginState,
	markdownToPmConverter?: MarkdownToPmConverter,
): Transaction => {
	let pasteStartPos = pluginState.pasteStartPos;
	const pasteEndPos = pluginState.pasteEndPos;
	const plaintext = pluginState.plaintext;

	if (pasteStartPos < 0) {
		return tr;
	}

	const resolvedPasteStartPos = tr.doc.resolve(pasteStartPos);
	const parentOffset = resolvedPasteStartPos.parentOffset;
	if (parentOffset === 0 && resolvedPasteStartPos.depth > 0) {
		pasteStartPos = resolvedPasteStartPos.before();
	}

	const markdownSlice: Slice | undefined = getMarkdownSlice(
		plaintext,
		tr.doc.type.schema,
		tr.selection,
		markdownToPmConverter,
	);

	if (!markdownSlice) {
		return tr;
	}

	pasteSliceIntoTransactionWithSelectionAdjust({
		tr,
		pasteStartPos,
		pasteEndPos,
		slice: markdownSlice,
	});

	return tr;
};

export const formatRichText = (
	tr: Transaction,
	pluginState: PasteOptionsPluginState,
): Transaction => {
	let pasteStartPos = pluginState.pasteStartPos;
	const pasteEndPos = pluginState.pasteEndPos;
	const richTextSlice = pluginState.richTextSlice;

	if (pasteStartPos < 0) {
		return tr;
	}

	if (richTextSlice.content.size === 0) {
		return tr;
	}

	const resolvedPasteStartPos = tr.doc.resolve(pasteStartPos);
	const parentOffset = resolvedPasteStartPos.parentOffset;
	if (parentOffset === 0 && resolvedPasteStartPos.depth > 0) {
		pasteStartPos = resolvedPasteStartPos.before();
	}

	richTextSliceTransactionWithSelectionAdjust({
		tr,
		pasteStartPos,
		pasteEndPos,
		slice: richTextSlice,
	});

	return tr;
};

export const formatPlainText = (
	tr: Transaction,
	pluginState: PasteOptionsPluginState,
): Transaction => {
	let pasteStartPos = pluginState.pasteStartPos;
	const pasteEndPos = pluginState.pasteEndPos;
	const plaintext = pluginState.plaintext;

	//not possible to create plain text slice with empty string
	if (pasteStartPos < 0 || plaintext === '') {
		return tr;
	}

	const resolvedPasteStartPos = tr.doc.resolve(pasteStartPos);
	const parentOffset = resolvedPasteStartPos.parentOffset;
	if (parentOffset === 0 && resolvedPasteStartPos.depth > 0) {
		pasteStartPos = resolvedPasteStartPos.before();
	}

	const schema = tr.doc.type.schema;
	const plainTextNode = schema.text(plaintext);
	const plainTextFragment = Fragment.from(
		schema.nodes.paragraph.createAndFill(null, plainTextNode),
	);

	const plainTextSlice = new Slice(
		plainTextFragment,
		resolvedPasteStartPos.depth,
		resolvedPasteStartPos.depth,
	);

	pasteSliceIntoTransactionWithSelectionAdjust({
		tr,
		pasteStartPos,
		pasteEndPos,
		slice: plainTextSlice,
	});

	return tr;
};

type PasteSliceIntoTransactionProps = {
	pasteEndPos: number;
	pasteStartPos: number;
	slice: Slice;
	tr: Transaction;
};
function pasteSliceIntoTransactionWithSelectionAdjust({
	tr,
	pasteStartPos,
	pasteEndPos,
	slice,
}: PasteSliceIntoTransactionProps) {
	tr.replaceRange(pasteStartPos, pasteEndPos, slice);

	// ProseMirror doesn't give a proper way to tell us where something was inserted.
	// However, we can know "how" it inserted something.
	//
	// So, instead of weird depth calculations, we can use the step produced by the transform.
	// For instance:
	//    The `replaceStep.to and replaceStep.from`, tell us the real position
	//    where the content will be insert.
	//    Then, we can use the `tr.mapping.map` to the updated position after the replace operation
	const replaceStep = tr.steps[0];

	if (!(replaceStep instanceof ReplaceStep)) {
		return tr;
	}

	const lastInsertNode = replaceStep.slice.content.lastChild;
	const emptyNodeReference = lastInsertNode?.type.createAndFill();
	const isLastNodeEmpty = emptyNodeReference?.nodeSize === lastInsertNode?.nodeSize;
	const isStepSplitingTarget = !lastInsertNode?.isLeaf && isLastNodeEmpty;

	const $nextHead = tr.doc.resolve(tr.mapping.map(replaceStep.to));

	const $nextPosition =
		isStepSplitingTarget && $nextHead.depth > 0 ? tr.doc.resolve($nextHead.before()) : $nextHead;

	// The findFrom will make search for both: TextSelection and NodeSelections.
	const nextSelection = Selection.findFrom($nextPosition, -1);

	if (nextSelection) {
		tr.setSelection(nextSelection);
	}
}
function richTextSliceTransactionWithSelectionAdjust({
	tr,
	pasteStartPos,
	pasteEndPos,
	slice,
}: PasteSliceIntoTransactionProps) {
	tr.replaceRange(pasteStartPos, pasteEndPos, slice);

	// ProseMirror doesn't give a proper way to tell us where something was inserted.
	// However, we can know "how" it inserted something.
	//
	// So, instead of weird depth calculations, we can use the step produced by the transform.
	// For instance:
	//    The `replaceStep.to and replaceStep.from`, tell us the real position
	//    where the content will be insert.
	//    Then, we can use the `tr.mapping.map` to the updated position after the replace operation
	const replaceStep = tr.steps[0];

	if (!(replaceStep instanceof ReplaceStep)) {
		return tr;
	}

	const nextPosition = tr.mapping.map(replaceStep.to);

	// The findFrom will make search for both: TextSelection and NodeSelections.
	const nextSelection = Selection.findFrom(
		tr.doc.resolve(Math.min(nextPosition, tr.doc.content.size)),
		-1,
	);

	if (nextSelection) {
		tr.setSelection(nextSelection);
	}
}

/**
 * Parse markdown text into a slice for paste replacement, using markdown-plus
 * only when the paste parser experiment is enabled.
 */
export function getMarkdownSlice(
	text: string,
	schema: Schema,
	selection: Selection,
	markdownToPmConverter?: MarkdownToPmConverter,
): Slice | undefined {
	const targetOpenStartNode = selection.$from.parent;
	const targetOpenEndNode = selection.$to.parent;

	try {
		let textInput: string = text;

		const textSplitByCodeBlock = textInput.split(CODE_BLOCK_FENCE_REGEX);

		for (let i = 0; i < textSplitByCodeBlock.length; i++) {
			if (i % 2 === 0) {
				// Ignored via go/ees005
				// eslint-disable-next-line require-unicode-regexp, @atlassian/perf-linting/no-expensive-split-replace -- Ignored via go/ees017 (to be fixed)
				textSplitByCodeBlock[i] = textSplitByCodeBlock[i].replace(/\\/g, '\\\\');
			}
		}

		textInput = textSplitByCodeBlock.join('```');

		const doc = markdownToPmConverter
			? schema.nodes.doc.createAndFill({}, markdownToPmConverter({ markdown: textInput, schema }))
			: new MarkdownTransformer(schema, md).parse(escapeLinks(textInput));

		if (!doc || !doc.content) {
			return;
		}
		const canMergeOpenStart = targetOpenStartNode.type === doc.content.firstChild?.type;
		const canMergeOpenEnd = targetOpenEndNode.type === doc.content.lastChild?.type;
		const $start = Selection.atStart(doc).$from;
		const $end = Selection.atEnd(doc).$from;

		const openStart = canMergeOpenStart ? $start.depth : 0;
		const openEnd = canMergeOpenEnd ? $end.depth : 0;

		return normalizeMarkdownCodeBlockAttrsInSlice(
			new Slice(doc.content, openStart, openEnd),
			schema,
		);
	} catch (error) {
		logException(error as Error, {
			location: 'editor-plugin-paste-options-toolbar/util',
		});
		return;
	}
}
